/**
 * Logged time is entered in MINUTE_STEP increments but stored as decimal hours (minutes / 60).
 * Reads round to the nearest minute, which absorbs float drift (12 × 5/60 = 0.99999…h → 60 min).
 */
export const MINUTE_STEP = 5;

export function hoursToMinutes(hours: number | string | null | undefined): number {
  const n = Number(hours);
  if (hours === null || hours === undefined || !Number.isFinite(n)) return 0;
  return Math.round(n * 60);
}

export function minutesToHours(minutes: number): number {
  return minutes / 60;
}

/** `1h 30m`, `30m`, `3h`, `0h`. */
export function formatMinutes(minutes: number): string {
  const { hours, minutes: m } = splitDuration(minutes);
  if (hours > 0 && m > 0) return `${hours}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${hours}h`;
}

export function formatDuration(hours: number | string | null | undefined): string {
  return formatMinutes(hoursToMinutes(hours));
}

/** New stored total after adding (or, when negative, removing) minutes; never below zero. */
export function addToTotal(
  currentHours: number | string | null | undefined,
  deltaMinutes: number
): number {
  return minutesToHours(Math.max(0, hoursToMinutes(currentHours) + deltaMinutes));
}

export function splitDuration(minutes: number): { hours: number; minutes: number } {
  return { hours: Math.floor(minutes / 60), minutes: minutes % 60 };
}

export function snapMinutes(minutes: number): number {
  return Math.round(minutes / MINUTE_STEP) * MINUTE_STEP;
}

/**
 * Move to the next grid point in delta's direction (an off-grid 6 steps to 10 or 5). When the result
 * would cross min/max it returns the input unchanged, so a caller can disable that button — or, with
 * clamp, lands on the bound (max rounded down to the grid) unless the input is already there.
 */
export function stepDuration(
  minutes: number,
  delta: number,
  { min = MINUTE_STEP, max, clamp = false }: { min?: number; max?: number; clamp?: boolean } = {}
): number {
  const next =
    delta > 0
      ? Math.floor(minutes / MINUTE_STEP) * MINUTE_STEP + delta
      : Math.ceil(minutes / MINUTE_STEP) * MINUTE_STEP + delta;
  if (next < min) return clamp && min < minutes ? min : minutes;
  if (max !== undefined && next > max) {
    const bound = Math.floor(max / MINUTE_STEP) * MINUTE_STEP;
    return clamp && bound > minutes ? bound : minutes;
  }
  return next;
}

export function validateDuration(minutes: number, { max }: { max?: number } = {}): string | null {
  if (!Number.isFinite(minutes) || minutes < MINUTE_STEP) return 'Enter at least 5 minutes.';
  if (minutes % MINUTE_STEP !== 0) return 'Use 5-minute steps.';
  if (max !== undefined && minutes > max) return `Can't be more than ${formatMinutes(max)}.`;
  return null;
}
